import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import { Button, Card, FormControl, FormLabel, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Enrollment } from './models/Enrollment';
import ConfirmExitDialog from './ConfirmExitDialog';
import TopNavActions from './TopNavActions'
import { registrationSiteNames } from './models/AllSites';

type Language = {
  name: string;
  displayName: string;
};

interface LanguageSelectionViewProps {
  enrollment: Enrollment;
  onPageChange: (page: string) => void;
}

const languages: Language[] = [
  { name: 'English', displayName: 'English' },
  { name: 'Spanish', displayName: 'Español' }
];

const LanguageSelectionView: React.FC<LanguageSelectionViewProps> = ({ enrollment, onPageChange }) => {
  const [selectedSite, setSelectedSite] = useState('');
  const { t, i18n } = useTranslation('enrollment');
  const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language === 'es-419' ? 'Spanish' : 'English');
  const [openExitDialog, setOpenExitDialog] = useState(false);
  const [siteDisabled, setSiteDisabled] = useState(false);

  // Change app language when selectedLanguage changes
  useEffect(() => {
    if (selectedLanguage === 'Spanish') {
      i18n.changeLanguage('es-419');
    } else {
      i18n.changeLanguage('en');
    }
  }, [selectedLanguage, i18n]);

  useEffect(() => {
    if (enrollment) {
      if (enrollment.language) {
        const lang = enrollment.language.toLowerCase();

        if (lang.includes('spanish') || lang.startsWith('es')) {
          setSelectedLanguage('Spanish');
        } else {
          setSelectedLanguage('English');
        }
      }

      if (enrollment.site && registrationSiteNames.find(site => site.name === enrollment.site)) {
        setSelectedSite(enrollment.site);
        setSiteDisabled(true);
      }
    }
  }, [enrollment]);

  const handleNext = () => {
    if (enrollment) {
      enrollment.site = selectedSite;
      enrollment.language = selectedLanguage;
    }
    onPageChange('accessibility-settings-enrollment-view');
  };

  const isDisabled = selectedLanguage === '' || selectedSite === '';

  return (
    <React.Fragment>
      <TopNavActions
        hideBack={true}
        onExit={() => setOpenExitDialog(true)}
      />
      <ConfirmExitDialog
        open={openExitDialog}
        onClose={() => setOpenExitDialog(false)}
        onExit={() => { window.location.href = '/'; }}
      />
      <Typography variant="h4" component='h1' id="page-heading" tabIndex={-1}>{t('Select a language and Site')}</Typography>
      <Card style={{padding: '0 1rem 1rem', marginBottom: '1rem'}}>
        <FormControl fullWidth margin="normal" component="fieldset">
          <FormLabel component="legend">
            <Typography variant="h6" component="label" htmlFor='language'>{t("Select a language")}</Typography>
          </FormLabel>
          <Select
            value={selectedLanguage}
            onChange={e => setSelectedLanguage(e.target.value)}
            name="language"
            inputProps={{ "id":"language", style: { display: 'none' } }}
          >
            {languages.map(language => (
              <MenuItem key={language.name} value={language.name}>
                {t(language.displayName)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Card>
      <Card style={{padding: '0 1rem 1rem', marginBottom: '1rem'}}>
        <FormControl fullWidth margin="normal" component="fieldset">
          <FormLabel component="legend">
            <Typography variant="h6" component="label" htmlFor="site">{t('Site')}</Typography>
          </FormLabel>
          <Select
            value={selectedSite}
            disabled={siteDisabled}
            onChange={e => setSelectedSite(e.target.value)}
            name="site"
            inputProps={{ "id":"site", style: { display: 'none' } }}
          >
            {registrationSiteNames.map(site => (
              <MenuItem key={site.name} value={site.name}>
                {site.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Card>
      <Button variant="contained" onClick={handleNext} disabled={isDisabled} style={{ marginTop: 32, marginBottom: 32 }}>
        {t('Next')}
      </Button>
    </React.Fragment>
  );
};

export default LanguageSelectionView;
